import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { User } from '../../types';
import { IMessage } from '../../services/messaging/IMessagingProvider';
import { MessageBubble } from './MessageBubble';
import { ChatInput } from './ChatInput';
import { useVideoCall } from '../../context/VideoCallContext';
import { useAppSelector } from '../../hooks/redux';

interface ChatWindowProps {
  recipient: User;
  currentUserId: string;
  messages: IMessage[];
  onSendMessage: (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string }) => Promise<void>;
  onEditMessage: (messageId: string, newText: string) => Promise<void>;
  onDeleteMessage: (messageId: string, scope: 'self' | 'everyone') => Promise<void>;
  onClose: () => void;
  isOnline: boolean;
  onCameraRequest: () => void;
  isTyping: boolean;
  onTyping: (isTyping: boolean) => void;
  lastSeen?: number;
  onSetReaction: (messageId: string, emoji: string) => Promise<void>;
  onRemoveReaction: (messageId: string) => Promise<void>;
}

// "Last seen X ago" formatting (Signal/WhatsApp-style)
function formatLastSeen(timestamp?: number): string | null {
  if (!timestamp) return null;
  const diffMs = Date.now() - timestamp;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Last seen just now';
  if (mins < 60) return `Last seen ${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Last seen ${hours}h ${String(mins % 60).padStart(2, '0')}m ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Last seen ${days}d ago`;
  return `Last seen ${new Date(timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  recipient,
  currentUserId,
  messages,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  onClose,
  isOnline,
  onCameraRequest,
  isTyping,
  onTyping,
  lastSeen,
  onSetReaction,
  onRemoveReaction
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Stable handlers for the memoized MessageBubbles. The parent passes fresh
  // inline lambdas every render; forwarding through a ref keeps the identity
  // fixed so unchanged bubbles don't redraw.
  const handlersRef = useRef({ onDeleteMessage, onSetReaction, onRemoveReaction });
  handlersRef.current = { onDeleteMessage, onSetReaction, onRemoveReaction };
  const handleDelete = useCallback(
    (id: string, scope: 'self' | 'everyone') => handlersRef.current.onDeleteMessage(id, scope), []);
  const handleSetReaction = useCallback(
    (id: string, emoji: string) => handlersRef.current.onSetReaction(id, emoji), []);
  const handleRemoveReaction = useCallback(
    (id: string) => handlersRef.current.onRemoveReaction(id), []);
  const handleStartEdit = useCallback(
    (id: string, content: string) => setEditingMessage({ id, content }), []);

  // Re-render periodically so "Last seen 1h 05m ago" keeps counting while idle
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (isOnline || !lastSeen) return;
    const id = setInterval(() => setClockTick(t => t + 1), 30_000);
    return () => clearInterval(id);
  }, [isOnline, lastSeen]);

  // Block common screenshot keyboard shortcuts while the chat window is mounted.
  // This covers: macOS Cmd+Shift+3/4/5, Windows PrtScn / Alt+PrtScn.
  // Cannot block OS-level shortcuts (hardware button, Win+PrtScn) — those bypass the browser.
  useEffect(() => {
    const block = (e: KeyboardEvent) => {
      const mac = e.metaKey && e.shiftKey && ['3', '4', '5', 's'].includes(e.key);
      const win = e.key === 'PrintScreen';
      if (mac || win) e.preventDefault();
    };
    window.addEventListener('keydown', block);
    return () => window.removeEventListener('keydown', block);
  }, []);

  // Privacy shield: blur the chat content when the window/tab loses focus or is
  // hidden. Covers screenshot tools (Win+Shift+S, Snipping Tool, alt-tabbing to a
  // capture app) and mobile app-switcher previews — all of which steal focus or
  // visibility before they can capture the screen. Signal Desktop does the same.
  const [isShielded, setIsShielded] = useState(false);
  useEffect(() => {
    const shield = () => setIsShielded(true);
    const unshield = () => { if (document.visibilityState === 'visible') setIsShielded(false); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') shield(); else unshield(); };
    window.addEventListener('blur', shield);
    window.addEventListener('focus', unshield);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', shield);
      window.removeEventListener('focus', unshield);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  const [sending, setSending] = useState(false);
  const [editingMessage, setEditingMessage] = useState<{ id: string; content: string } | null>(null);
  const prevMessageCountRef = useRef(0);
  const initialLoadRef = useRef(true);

  // Clear edit mode if the conversation changes or the message being edited disappears
  useEffect(() => { setEditingMessage(null); }, [recipient.id]);
  useEffect(() => {
    if (editingMessage && !messages.some(m => m.id === editingMessage.id)) setEditingMessage(null);
  }, [messages, editingMessage]);

  const handleSaveEdit = async (messageId: string, newText: string) => {
    await onEditMessage(messageId, newText);
  };

  // Reset scroll state when the conversation changes
  useEffect(() => {
    prevMessageCountRef.current = 0;
    initialLoadRef.current = true;
  }, [recipient.id]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    const prev = prevMessageCountRef.current;
    const isInitialLoad = initialLoadRef.current && prev === 0;
    const isSingleNew = !initialLoadRef.current && messages.length === prev + 1;

    prevMessageCountRef.current = messages.length;

    if (isInitialLoad) {
      // Jump instantly to bottom — no animation, no visible scroll
      initialLoadRef.current = false;
      container.scrollTop = container.scrollHeight;
    } else if (isSingleNew) {
      // Only one new message arrived — smooth scroll
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Bulk load (e.g. switching conversation) — instant
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (content: string, mediaData?: { mediaType: string; mediaUrl: string; thumbnailUrl?: string; mediaKey?: string; mediaMimeType?: string }) => {
    setSending(true);
    try {
      await onSendMessage(content, mediaData);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const { startCall, callState } = useVideoCall();
  const { loginUser } = useAppSelector((state) => state.user);
  const displayName = recipient.displayName || recipient.display_name || recipient.email;
  const avatarUrl = recipient.photoURL || recipient.photo_url;

  return (
    <div className="chat-window" style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
      background: 'var(--background-default)',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {isShielded && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--background-default)',
          backdropFilter: 'blur(20px)',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          color: 'var(--text-secondary)'
        }}>
          <span className="material-icons" style={{ fontSize: '40px', opacity: 0.5 }}>visibility_off</span>
          <div>Content hidden</div>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .chat-window .card {
            border-left: none !important;
            border-right: none !important;
          }
        }
        @media (min-width: 769px) {
          .chat-header-avatar { display: none !important; }
          .chat-header-status { display: none !important; }
        }
      `}</style>
      {/* Header */}
      <div className="card" style={{ borderRadius: '0', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        <div className="card-content" style={{ padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="chat-header-avatar">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      boxShadow: '0 0 0 2px rgba(255,255,255,0.7)'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'var(--primary-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.7)'
                  }}>
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                  {displayName}
                </div>
                <div className="chat-header-status" style={{
                  fontSize: '12px',
                  color: isOnline ? 'var(--success-color)' : 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {!isTyping && (
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: isOnline ? 'var(--success-color)' : 'var(--text-secondary)'
                    }} />
                  )}
                  {isTyping
                    ? <span style={{ fontStyle: 'italic' }}>Typing…</span>
                    : isOnline
                      ? 'Online'
                      : (formatLastSeen(lastSeen) || 'Offline')}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                className="icon-btn"
                onClick={() => startCall(String(recipient.id), {
                  userId: String(loginUser?.id),
                  name: loginUser?.display_name || loginUser?.email || 'Unknown',
                  avatar: loginUser?.photo_url || undefined,
                })}
                disabled={callState !== 'idle'}
                style={{ color: callState === 'idle' ? 'var(--primary-color)' : 'var(--text-secondary)', transition: 'all 0.2s ease' }}
                title="Start video call"
              >
                <span className="material-icons">videocam</span>
              </button>
              <button
                className="icon-btn"
                onClick={onClose}
                style={{ color: 'var(--text-primary)', transition: 'all 0.2s ease' }}
                title="Close chat"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        position: 'relative'
      }}>
        {messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: 'var(--text-secondary)',
            marginTop: '32px'
          }}>
            <span className="material-icons" style={{ fontSize: '48px', opacity: 0.3 }}>
              chat_bubble_outline
            </span>
            <div style={{ marginTop: '8px' }}>No messages yet. Start the conversation!</div>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwn={String(message.senderId) === String(currentUserId)}
              currentUserId={currentUserId}
              isBeingEdited={editingMessage?.id === message.id}
              onStartEdit={String(message.senderId) === String(currentUserId) ? handleStartEdit : undefined}
              onDelete={handleDelete}
              onSetReaction={handleSetReaction}
              onRemoveReaction={handleRemoveReaction}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ flexShrink: 0 }}>
        <ChatInput
          onSend={handleSend}
          disabled={sending}
          onCameraRequest={onCameraRequest}
          editingMessage={editingMessage}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={() => setEditingMessage(null)}
          onTyping={onTyping}
        />
      </div>
    </div>
  );
};
